import {
  FlexPositionActionType,
  PositionActionKey,
  PositionActionRequest,
} from '../models/position-action.model'
import { RowOrigin } from '../models/source-key.model'
import { WorkbenchRow } from '../models/workbench-row.model'

/**
 * Turns "the user clicked this menu item on these rows" into the `POST /flex-positions/actions`
 * body, which is not a rename away: the endpoint splits its ids by FEED, and its four menu actions
 * collapse onto three wire actions.
 *
 * Kept out of the grid component so the mapping — the part that has to stay pinned to the backend
 * contract — is unit-testable without standing up ag-Grid.
 */

/**
 * Which wire `actionType` each menu action posts.
 *
 * `unignore` is the one that is not an identity mapping. The endpoint has no `unignore`: an ignore
 * is stored as an ordinary match group pairing the row against a synthetic `"none"` counterpart, so
 * dissolving that group — an `unmatch` — is what reverses it (helios !491, "unmatch reverses an
 * ignore").
 */
export const WIRE_ACTION: Record<PositionActionKey, FlexPositionActionType> = {
  match: 'match',
  unmatch: 'unmatch',
  ignore: 'ignore',
  unignore: 'unmatch',
}

/**
 * An IGNORE is Prometheus-only on both sides now, and its body carries one id set:
 *
 *     { "actionType": "ignore", "promPosIds": ["216338922807"] }
 *
 * `enfnPosIds` is OMITTED, not sent empty. That is the confirmed shape the endpoint accepts, and it
 * matches where the design puts the gesture (`Positions-2.0-Feature-Differences.md` §4; the
 * prototype's `setIgnore` skips any row whose `src` is not `cust`/`trad`). An earlier revision of
 * this file documented the two as disagreeing — the endpoint previously read `enfnPosIds` only for
 * an ignore — which is no longer the case; do not reintroduce an Enfusion side here.
 *
 * Omission is the general rule, not an ignore special case: {@link omitEmpty} drops any id set with
 * nothing in it, so a request states only the feeds it actually names. Safe for every action
 * because both fields default to `emptySet()` server-side, so an omitted set and `[]` deserialize
 * identically — a `match` is unaffected in practice since it requires both sides to be non-empty.
 */

/** The Enfusion feed — `enfnPosIds` server-side. */
const ENFN_ORIGINS: RowOrigin[] = ['api']

/** The Prometheus feed, both of its sub-origins — `promPosIds` server-side. */
const PROM_ORIGINS: RowOrigin[] = ['cust', 'trad']

/**
 * What a click resolves to. `blocked` is a refusal the CLIENT makes, before any request: the
 * selection cannot produce a well-formed body, so posting it would buy a guaranteed 400 and a
 * confusing error. `reason` is written to be shown to the user as-is.
 */
export type PositionActionPlan =
  | { kind: 'send'; request: PositionActionRequest }
  | { kind: 'blocked'; reason: string }

/** Whether a row belongs to a feed the actions endpoint knows about at all. */
const isKnownFeed = (row: WorkbenchRow): boolean =>
  ENFN_ORIGINS.includes(row.origin) || PROM_ORIGINS.includes(row.origin)

const idsOf = (rows: readonly WorkbenchRow[], origins: RowOrigin[]): string[] =>
  // Set, because the server's fields are `Set<String>` — a duplicate id is not an error there, it
  // just makes the request wider than the intent.
  [
    ...new Set(
      rows.filter(row => origins.includes(row.origin)).map(row => row.rowId),
    ),
  ]

/**
 * Build the request body for one menu action over one set of rows.
 *
 * Three client-side refusals, each standing in for a 400 the server would otherwise return:
 *
 *  - **Clearwater rows.** `flexpositions.positions` holds the Enfusion and Prometheus feeds only,
 *    so a Clearwater row has no id the endpoint can resolve and would come back `rowsNotFound`.
 *    Unreachable today (the registry holds the Clearwater grid back — `disabledRoles: ['base']`),
 *    but the context menu renders Match/Unmatch on that source, so the guard is not theoretical.
 *  - **A match missing a side.** The server's own `bothSidesRequired`, checked here so the message
 *    can name what is missing.
 *  - **An ignore over Enfusion rows.** Ignore is a Prometheus gesture per the design's action
 *    matrix, so an Enfusion row in the scope is not something to quietly drop.
 *
 * Everything else — already-matched, cross-account, unsupported position type, a group span the
 * server will not merge — is left to the endpoint, which is the authority and returns a
 * distinguishable `validationId` for each (see `utils/action-error.ts`).
 */
export function buildPositionActionRequest(
  action: PositionActionKey,
  rows: readonly WorkbenchRow[],
): PositionActionPlan {
  if (!rows.length) {
    return { kind: 'blocked', reason: 'No positions are selected.' }
  }

  if (rows.some(row => !isKnownFeed(row))) {
    return {
      kind: 'blocked',
      reason:
        'Clearwater positions cannot be matched from this panel — select Enfusion and Prometheus positions instead.',
    }
  }

  const enfnPosIds = idsOf(rows, ENFN_ORIGINS)
  const promPosIds = idsOf(rows, PROM_ORIGINS)
  const actionType = WIRE_ACTION[action]

  if (action === 'ignore' || action === 'unignore') {
    if (enfnPosIds.length) {
      return {
        kind: 'blocked',
        reason: `Only Prometheus positions can be ${
          action === 'ignore' ? 'ignored' : 'unignored'
        }.`,
      }
    }
    return { kind: 'send', request: omitEmpty(actionType, [], promPosIds) }
  }

  if (action === 'match' && (!enfnPosIds.length || !promPosIds.length)) {
    return {
      kind: 'blocked',
      reason:
        'A match needs at least one Enfusion position and one Prometheus position.',
    }
  }

  return {
    kind: 'send',
    request: omitEmpty(actionType, enfnPosIds, promPosIds),
  }
}

/**
 * Assemble the body with the empty id sets left OUT rather than sent as `[]`.
 *
 * Not cosmetic: an ignore's accepted shape is `{ actionType, promPosIds }`, and a key present with
 * an empty value reads on the wire as "I looked at this feed and found nothing", which is a
 * different statement from "this action has no such side". Both deserialize to `emptySet()`, so
 * nothing downstream changes — the request just stops claiming something it does not mean.
 */
function omitEmpty(
  actionType: FlexPositionActionType,
  enfnPosIds: string[],
  promPosIds: string[],
): PositionActionRequest {
  return {
    actionType,
    ...(enfnPosIds.length ? { enfnPosIds } : {}),
    ...(promPosIds.length ? { promPosIds } : {}),
  }
}
